import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ModelKeyForm } from '@models/competition/model-parameterization/forms/model-key-form';
import { ClashFilter } from '@models/clash/clash-form.dto';
import {
  buildChaveEmbateForm,
  ChaveEmbateFg,
  ChaveEmbateForm,
  createChaveEmbateFgFromDto,
} from '@models/competition/step-paramaterization/form/chave-embate-form';
import { ClashDto } from '@models/embate/clash-dto';
import { IPage } from '@models/pagination/page';
import { ClashService } from '@shared/services/competition/clash.service';
import { ControlsOf } from '@shared/types/form-group-of';
import { LoadingService } from '@shared/services/loading.service';
import { BehaviorSubject, finalize } from 'rxjs';
import { BaseComponent } from 'src/app/core/utilities/base-component/base-component';

@Component({
  selector: 'app-key-generation',
  templateUrl: './key-generation.component.html',
})
export class KeyGenerationComponent extends BaseComponent implements OnChanges {
  @Input() amountOfKeysGenerated?: number;
  @Input() petNrId?: number;
  @Input() petBlockEdit?: boolean;
  @Input() modelConfiguration = false;
  @Input() modelKeyForms?: FormArray<FormGroup<ControlsOf<ModelKeyForm>>>;
  @Input() showError?: (key: string) => string | undefined;
  @Output() keyInfoOutput = new EventEmitter<ChaveEmbateForm[]>();

  modelKeysBeingEdited = new Set<number>();
  cheNrIdEdit?: number;
  modalEditKeyName: boolean = false;
  keysDto?: IPage<ClashDto>;

  keyNameForm = this.fb.group({
    cheTxNome: this.fb.control<string>('', [
      Validators.required,
      this.stringSizeValidator(4),
    ]),
  });

  keyFormGroup = this.fb.group({
    keyList: this.fb.array<FormGroup<ChaveEmbateFg>>([]),
  });

  get keyForm(): FormArray<FormGroup<ChaveEmbateFg>> {
    return this.keyFormGroup.controls.keyList;
  }

  public filters = new BehaviorSubject<ClashFilter>({
    cheTxNome: '',
  });

  readonly #clashService = inject(ClashService);
  saveNameLoading: boolean = false;

  inputErrorMessage: string | undefined;

  ngOnInit() {
    if (this.modelConfiguration) return;
    this.filters.next({ ...this.filters.getValue(), petNrId: this.petNrId });
    this.#loadClash();
  }

  trackByCheNrId(index: number, fg: FormGroup<ChaveEmbateFg>) {
    return fg.controls.cheNrId.value;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['petBlockEdit']) {
      this.petBlockEdit = changes['petBlockEdit'].currentValue;
    }
  }

  editModelKeyName(index: number): void {
    this.modelKeysBeingEdited.add(index);
  }

  finishEditingModelKeyName(index: number): void {
    const control = this.modelKeyForms?.at(index).controls.chvTxNome;
    if (!control) return;
    if (!control.value?.trim()) control.setValue(this.defaultModelKeyName(index));
    this.modelKeysBeingEdited.delete(index);
  }

  private defaultModelKeyName(index: number): string {
    let n = index;
    let letters = '';
    do {
      letters = String.fromCharCode(65 + (n % 26)) + letters;
      n = Math.floor(n / 26) - 1;
    } while (n >= 0);
    return `Chave ${letters}`;
  }

  #loadClash(page?: number) {
    this.keyForm.clear();
    this.#clashService
      .getClashs(this.filters.getValue()!, { page: page ?? 0, size: 10 })
      .subscribe({
        next: (response) => {
          this.keysDto = response;
          this.amountOfKeysGenerated = response.totalElements ?? 0;
          response.content?.forEach((dto) =>
            this.keyForm.push(createChaveEmbateFgFromDto(dto, this.fb)),
          );
          this.keyForm.updateValueAndValidity();
          this.emitKey();
        },
        error: (err) => {
          this.messageErrorResolver.resolveMessage(err);
        },
      });
  }
  validStringSize(size: number): string {
    let value = this.keyNameForm.get('cheTxNome')?.value;
    if (value && value.replace(/\s/g, '').length < 4) {
      return 'Mínimo de ' + size + ' caracteres';
    }
    return '';
  }
  stringSizeValidator(size: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const trimmedLength = control.value.replace(/\s/g, '').length;

      if (trimmedLength < size) {
        return {
          validStringSize: {
            requiredLength: size,
            actualLength: trimmedLength,
          },
        };
      }

      return null;
    };
  }

  onPageChange(page: number) {
    this.#loadClash(page);
  }

  searchKeys(cheTxNome: string) {
    this.filters.next({ ...this.filters.getValue(), cheTxNome: cheTxNome });
    this.#loadClash();
  }

  openModalEditKeyName(cheNrId: number, cheTxNome: string) {
    this.cheNrIdEdit = cheNrId;
    this.keyNameForm.controls['cheTxNome'].setValue(cheTxNome);
    this.modalEditKeyName = true;
  }

  closeModalEditKeyName() {
    this.emitKey();
    this.modalEditKeyName = false;
  }

  chaveEmbateFormBuilder(
    fg: FormArray<FormGroup<ChaveEmbateFg>>,
  ): ChaveEmbateForm[] {
    const values = fg.value.map((value) =>
      buildChaveEmbateForm({
        cheNrId: value.cheNrId || undefined,
        cheTxNome: value.cheTxNome || undefined,
        cheNrEtapa: value.cheNrEtapa || undefined,
        cheBlEmbGerados: value.cheBlEmbGerados || undefined,
        cheNrQtdInc: value.cheNrQtdInc || undefined,
        cheNrQtdVencedor: value.cheNrQtdVencedor || undefined,
      }),
    );

    values.forEach((value) => {
      const dto = this.keysDto?.content?.find((dtoValue) => {
        return dtoValue.cheNrId == value.entNrId;
      });

      value.verifyChanges(dto);
    });

    return values;
  }

  emitKey() {
    const value = this.chaveEmbateFormBuilder(this.keyForm);
    this.keyInfoOutput.emit(value);
  }

  editKeyName() {
    this.saveNameLoading = true;
    const keyName = this.keyNameForm.controls['cheTxNome'].value!;
    this.#clashService
      .configureClash({
        cheTxNome: keyName,
        cheNrId: this.cheNrIdEdit!,
        petNrId: this.petNrId!,
      })
      .pipe(
        finalize(() => {
          this.saveNameLoading = false;
        }),
      )
      .subscribe({
        next: () => {
          const fg = this.keyForm.controls.find(
            (fg) => fg.controls.cheNrId.value === this.cheNrIdEdit,
          );

          fg?.patchValue({ cheTxNome: keyName });

          this.closeModalEditKeyName();
          this.snackbarService.showSuccess('Nome da chave editado com sucesso');
        },
        error: (err) => {
          this.messageErrorResolver.resolveMessage(err);
        },
      });
  }
}
